import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, TextField, Grid, Typography, Divider, Switch,
  Button, Alert, FormControlLabel, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Paper, IconButton, Chip, MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import { useAuthContext } from '../hooks/useAuthContext';
import { DMIC_GAIN_OPTIONS, adcVolDb, dmicTotalGainDb, clipPeakG, formatG, formatDb } from '../utils/dmicGain';

const HPF_OPTIONS = [
  { value: -2, label: 'Auto (~1.25 Hz)' },
  { value: -1, label: 'Off' },
  ...[0, 1, 2, 3, 4, 5, 6, 7].map((idx) => ({ value: idx, label: String(idx) })),
];

const MIC_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#e6ffed',
  },
};

// New firmware replaces record_audio (bool) with record_trigger
const RECORD_TRIGGER_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'continuous', label: 'Continuous' },
  { value: 'auto', label: 'Auto (schedule or SPL threshold)' },
];

// Firmware defaults for schedule rule fields that are missing (record_audio.cpp reload_record_schedules);
// a missing threshold falls back to spl_upload_threshold
const SCHEDULE_DEFAULTS = {
  name: 'default',
  enabled: true,
  weekdays: 62, // Mon-Fri
  start_time: '08:00',
  end_time: '18:00',
  mode: 0, // 0 = fixed interval, 1 = SPL threshold
  interval: 1800,
};
// Schedule rule modes (firmware D27); only apply with record_trigger "auto" and the schedule on
const SCHEDULE_MODE_OPTIONS = [
  { value: 0, label: '0 Interval', help: 'Records one clip every interval, regardless of level.' },
  { value: 1, label: '1 Threshold', help: 'Records when TI SPL exceeds the threshold, then waits the interval (cooldown) before recording again.' },
  { value: 2, label: '2 Interval + Threshold', help: 'Records at least once every interval, and whenever SPL exceeds the threshold with no cooldown. While it stays loud it keeps recording back to back until it is quiet.' },
];
// Mode 3 (old "both") was removed; it meant interval + threshold
const normalizeScheduleMode = (mode) => (mode === 3 ? 2 : mode);

// Firmware ignores rules beyond this count
const MAX_SCHEDULE_RULES = 16;

const WEEKDAY_OPTIONS = [
  { bit: 1, label: 'Sun' },
  { bit: 2, label: 'Mon' },
  { bit: 4, label: 'Tue' },
  { bit: 8, label: 'Wed' },
  { bit: 16, label: 'Thu' },
  { bit: 32, label: 'Fri' },
  { bit: 64, label: 'Sat' },
];

const DeviceSetting = React.forwardRef(({ device, onSaveSettings }, ref) => {
  const client_id = device?.clientId;
  const [deviceConfig, setDeviceConfig] = useState({
    client_name: '',
    mqtt_server: '',
    mqtt_port: 0,
    post_server: '',
    post_port: 0,
    imei: '',
    iccid: '',
    record_time: 0,
    record_device_id: 1,
    record_sample_rate: 16000,
    record_bit_depth: 16,
    record_mode: 'single',
    wifi_enable: false,
    wifi_ssid: '',
    wifi_password: '',
    ethernet_enable: false,
    ethernet_dhcp: true,
    ethernet_ip: '',
    ethernet_mask: '',
    ethernet_gateway: '',
    ethernet_dns: '',
    ntp_server: '',
    ntp_port: 0,
    ntp_time_zone: 8,
    spl_report_interval: 60,
    spl_upload_threshold: 0,
    gps_enable: false,
    longitude: 0,
    latitude: 0,
    wind_speed_id: 1,
    wind_direction_id: 2,
    wind_direction_timeout: 10,
    wind_baud_rate: 9600,
    wind_direction_enable: false,
    speed_id: 1,
    direction_id: 2,
    baurad_rate: 9600,
    record_schedule_enable: false,
    record_schedules: [],
  });
  // Firmware without record_mode in its config does not support dual recording
  const [recordModeSupported, setRecordModeSupported] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { user } = useAuthContext();
  const userToken = user?.token;

  useEffect(() => {
    let isCancelled = false;
    
    const fetchDeviceConfig = async () => {
      if (!client_id) return;

      try {
        setLoading(true);
        const response = await fetch(`/api/v1/device/config/${client_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`,
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Fetched device config:', data.device_config);

        if (data && data.device_config) {
          const cfg = data.device_config;
          setRecordModeSupported(cfg.record_mode !== undefined);
          setDeviceConfig(prevConfig => ({
            ...prevConfig,
            ...cfg,
            record_sample_rate: cfg.record_sample_rate ?? prevConfig.record_sample_rate ?? 16000,
            record_bit_depth: cfg.record_bit_depth ?? prevConfig.record_bit_depth ?? 16,
            record_mode: cfg.record_mode === 'dual' ? 'dual' : 'single',
            record_schedule_enable: cfg.record_schedule_enable ?? prevConfig.record_schedule_enable ?? false,
            record_schedules: Array.isArray(cfg.record_schedules)
              ? cfg.record_schedules.map((rule) => (rule.mode === 3 ? { ...rule, mode: 2 } : rule))
              : (prevConfig.record_schedules || []),
            spl_report_interval: cfg.spl_report_interval ?? cfg.spl_update_interval ?? prevConfig.spl_report_interval ?? 60,
            spl_upload_threshold: cfg.spl_upload_threshold ?? prevConfig.spl_upload_threshold ?? 0,
            wind_speed_id: cfg.wind_speed_id ?? cfg.speed_id ?? prevConfig.wind_speed_id,
            wind_direction_id: cfg.wind_direction_id ?? cfg.direction_id ?? prevConfig.wind_direction_id,
            wind_baud_rate: cfg.wind_baud_rate ?? cfg.baurad_rate ?? prevConfig.wind_baud_rate,
            wind_direction_timeout: cfg.wind_direction_timeout ?? prevConfig.wind_direction_timeout,
            speed_id: cfg.wind_speed_id ?? cfg.speed_id ?? prevConfig.speed_id,
            direction_id: cfg.wind_direction_id ?? cfg.direction_id ?? prevConfig.direction_id,
            baurad_rate: cfg.wind_baud_rate ?? cfg.baurad_rate ?? prevConfig.baurad_rate,
          }));
        }
        
        setError(null);
      } catch (err) {
        if (!isCancelled) {
          console.error("Error fetching device config:", err);
          setError("Failed to load device settings");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchDeviceConfig();
    
    return () => {
      isCancelled = true;
    };
  }, [client_id, userToken]);

  // Rules sharing a name share the firmware's recording cooldown
  const scheduleNameCounts = (deviceConfig.record_schedules || []).reduce((counts, rule) => {
    const name = rule.name ?? SCHEDULE_DEFAULTS.name;
    counts[name] = (counts[name] || 0) + 1;
    return counts;
  }, {});
  const hasDuplicateScheduleNames = Object.values(scheduleNameCounts).some((n) => n > 1);

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();

    if (hasDuplicateScheduleNames) {
      setError("Schedule rule names must be unique");
      return false;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(`/api/v1/device/config/${client_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          device_config: deviceConfig
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);

      if (typeof onSaveSettings === 'function') {
        console.log("Calling onSaveSettings with deviceConfig:", deviceConfig);
        onSaveSettings(deviceConfig);
      }
      
      return true;
    } catch (err) {
      console.error("Error updating device config:", err);
      setError("Failed to update device settings, please try again later");
      return false;
    } finally {
      setLoading(false);
    }
  }, [deviceConfig, client_id, userToken, onSaveSettings, hasDuplicateScheduleNames]);

  React.useImperativeHandle(ref, () => {
    return {
      submit: () => {
        console.log("submit called via ref, current deviceConfig:", deviceConfig);
        return handleSubmit();
      }
    };
  }, [handleSubmit]);

  // Edit a field inside mic_config (new firmware), e.g. ('realtek', 'adc_vol')
  const handleMicChange = (chip, field) => (e) => {
    const value = Number(e.target.value);
    setDeviceConfig(prev => ({
      ...prev,
      mic_config: {
        ...prev.mic_config,
        [chip]: {
          ...(prev.mic_config?.[chip] || {}),
          [field]: value
        }
      }
    }));
  };

  // record_trigger present = new firmware; only record_audio = legacy firmware
  const hasRecordTrigger = deviceConfig.record_trigger !== undefined;
  const recordTrigger = deviceConfig.record_trigger;
  const scheduleActive = !hasRecordTrigger || recordTrigger === 'auto';
  // Firmware gives enabled mode 1 (threshold) rules without their own threshold spl_upload_threshold
  const schedulesUseDefaultThreshold = !!deviceConfig.record_schedule_enable &&
    (deviceConfig.record_schedules || []).some((rule) =>
      (rule.enabled ?? SCHEDULE_DEFAULTS.enabled) &&
      [1, 2].includes(normalizeScheduleMode(rule.mode ?? SCHEDULE_DEFAULTS.mode)) &&
      (rule.threshold === undefined || rule.threshold === null || rule.threshold === ''));
  const thresholdActive = hasRecordTrigger
    ? recordTrigger === 'auto' && (!deviceConfig.record_schedule_enable || schedulesUseDefaultThreshold)
    : true;
  const scheduleDefaultHelp = 'Default for mode 1 / 2 schedule rules without their own threshold';
  const thresholdHelp = !hasRecordTrigger
    ? (schedulesUseDefaultThreshold
      ? scheduleDefaultHelp
      : 'Old firmware: records above this level even when audio recording is off')
    : !thresholdActive
      ? (recordTrigger === 'auto'
        ? 'Not used: every enabled mode 1 / 2 schedule rule sets its own threshold'
        : `Not used in ${recordTrigger} mode`)
      : deviceConfig.record_schedule_enable
        ? scheduleDefaultHelp
        : 'Records when TI SPL exceeds this level';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked :
        (type === 'number' ? Number(value) : value);

    setDeviceConfig(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      if (name === 'wind_speed_id') updated.speed_id = newValue;
      if (name === 'wind_direction_id') updated.direction_id = newValue;
      if (name === 'wind_baud_rate') updated.baurad_rate = newValue;
      if (name === 'speed_id') updated.wind_speed_id = newValue;
      if (name === 'direction_id') updated.wind_direction_id = newValue;
      if (name === 'baurad_rate') updated.wind_baud_rate = newValue;
      return updated;
    });
  };

  const handleAddScheduleItem = () => {
    setDeviceConfig(prev => {
      const currentList = Array.isArray(prev.record_schedules) ? prev.record_schedules : [];
      if (currentList.length >= MAX_SCHEDULE_RULES) return prev;
      // Firmware tracks each rule's cooldown by name, so names must be unique
      const usedNames = new Set(currentList.map((item) => item.name ?? SCHEDULE_DEFAULTS.name));
      let newIndex = currentList.length + 1;
      while (usedNames.has(`schedule_${newIndex}`)) newIndex += 1;
      const newItem = {
        name: `schedule_${newIndex}`,
        enabled: true,
        weekdays: 127,
        start_time: '08:00',
        end_time: '18:00',
        mode: 1,
        threshold: 70.0,
        interval: 1800,
      };
      return {
        ...prev,
        record_schedules: [...currentList, newItem]
      };
    });
  };

  const handleRemoveScheduleItem = (index) => {
    setDeviceConfig(prev => {
      const currentList = Array.isArray(prev.record_schedules) ? prev.record_schedules : [];
      const updatedList = currentList.filter((_, i) => i !== index);
      return {
        ...prev,
        record_schedules: updatedList
      };
    });
  };

  const handleScheduleItemChange = (index, field, value) => {
    setDeviceConfig(prev => {
      const currentList = Array.isArray(prev.record_schedules) ? prev.record_schedules : [];
      const updatedList = currentList.map((item, i) => {
        if (i === index) {
          return { ...item, [field]: value };
        }
        return item;
      });
      return {
        ...prev,
        record_schedules: updatedList
      };
    });
  };

  const handleScheduleWeekdayToggle = (index, bit) => {
    setDeviceConfig(prev => {
      const currentList = Array.isArray(prev.record_schedules) ? prev.record_schedules : [];
      const updatedList = currentList.map((item, i) => {
        if (i === index) {
          const currentWeekdays = typeof item.weekdays === 'number' ? item.weekdays : SCHEDULE_DEFAULTS.weekdays;
          const newWeekdays = (currentWeekdays & bit) !== 0 ? (currentWeekdays ^ bit) : (currentWeekdays | bit);
          return { ...item, weekdays: newWeekdays };
        }
        return item;
      });
      return {
        ...prev,
        record_schedules: updatedList
      };
    });
  };

  if (loading && !deviceConfig.client_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Device settings updated successfully
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Divider textAlign="left" sx={{ my: 2 }}>Basic Settings</Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Device Name"
              name="client_name"
              value={deviceConfig.client_name || ''}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#e6ffed', 
                },
              }}
            />
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>Location Settings</Divider>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Tooltip title="Get location from the GPS module" placement="top-start">
              <FormControlLabel
                control={
                  <Switch
                    checked={deviceConfig.gps_enable || false}
                    onChange={handleChange}
                    name="gps_enable"
                    color="primary"
                  />
                }
                label="Enable GPS"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={6}>
            <Tooltip
              title={deviceConfig.gps_enable ? "Provided by GPS; disable GPS to set manually" : "Longitude of the device"}
              placement="top-start"
            >
              <TextField
                fullWidth
                label="Longitude"
                name="longitude"
                type="number"
                value={deviceConfig.longitude || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                InputProps={{
                  readOnly: !!deviceConfig.gps_enable,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: deviceConfig.gps_enable ? undefined : '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={6}>
            <Tooltip
              title={deviceConfig.gps_enable ? "Provided by GPS; disable GPS to set manually" : "Latitude of the device"}
              placement="top-start"
            >
              <TextField
                fullWidth
                label="Latitude"
                name="latitude"
                type="number"
                value={deviceConfig.latitude || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                InputProps={{
                  readOnly: !!deviceConfig.gps_enable,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: deviceConfig.gps_enable ? undefined : '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>MQTT Settings</Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="MQTT Server"
              name="mqtt_server"
              value={deviceConfig.mqtt_server || ''}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="MQTT Port"
              name="mqtt_port"
              type="number"
              value={deviceConfig.mqtt_port || ''}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>POST Settings</Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="POST Server"
              name="post_server"
              value={deviceConfig.post_server || ''}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="POST Port"
              name="post_port"
              type="number"
              value={deviceConfig.post_port || ''}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>Recording Settings</Divider>
        <Grid container spacing={2}>
          {hasRecordTrigger ? (
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Record Trigger"
                name="record_trigger"
                value={recordTrigger}
                onChange={handleChange}
                helperText={{
                  off: 'No recording at all, schedules included',
                  continuous: 'Records back to back, ignoring schedules and threshold',
                  auto: 'Follows the schedule when enabled, otherwise the SPL threshold',
                }[recordTrigger] || 'Unknown value; the device treats it as Auto'}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              >
                {RECORD_TRIGGER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deviceConfig.record_audio || false}
                    onChange={handleChange}
                    name="record_audio"
                  />
                }
                label="Enable Audio Recording"
              />
              <Typography variant="body2" color="text.secondary">
                When off, this firmware still records when SPL exceeds the threshold below.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Tooltip title="Recording duration in seconds" placement="top-start">
              <TextField
                fullWidth
                label="Recording Duration (seconds)"
                name="record_time"
                type="number"
                value={deviceConfig.record_time || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip title="1 for Realtek, 2 for TI audio codec" placement="top-start">
              <TextField
                fullWidth
                label="Recording Device ID"
                name="record_device_id"
                type="number"
                value={deviceConfig.record_device_id || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip title="Audio recording sampling rate in Hz (e.g. 16000)" placement="top-start">
              <TextField
                fullWidth
                label="Recording Sample Rate (Hz)"
                name="record_sample_rate"
                type="number"
                value={deviceConfig.record_sample_rate ?? ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip title="Audio recording bit depth (e.g. 16, 24, 32)" placement="top-start">
              <TextField
                fullWidth
                label="Recording Bit Depth (bits)"
                name="record_bit_depth"
                type="number"
                value={deviceConfig.record_bit_depth ?? 16}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip title="Single or dual channel recording" placement="top-start">
              <TextField
                select
                fullWidth
                label="Recording Mode"
                name="record_mode"
                value={deviceConfig.record_mode || 'single'}
                onChange={handleChange}
                helperText={recordModeSupported
                  ? 'Takes effect after the device reboots'
                  : 'This firmware does not report record_mode; it records in single mode'}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              >
                <MenuItem value="single">Single</MenuItem>
                <MenuItem value="dual">Dual</MenuItem>
              </TextField>
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <Tooltip 
              title="When audio recording is enabled, files will be uploaded if the SPL exceeds this threshold." 
              placement="top-start"
            >
              <TextField
                fullWidth
                label="SPL Threshold for Upload (dBA)"
                name="spl_upload_threshold"
                type="number"
                value={deviceConfig.spl_upload_threshold || 0}
                onChange={handleChange}
                disabled={!thresholdActive}
                helperText={thresholdHelp}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon fontSize="small" />
            <span>Schedule Settings</span>
          </Box>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={deviceConfig.record_schedule_enable || false}
                  onChange={handleChange}
                  name="record_schedule_enable"
                  color="primary"
                  disabled={!scheduleActive}
                />
              }
              label="Enable Recording Schedule"
            />
            {!scheduleActive && (
              <Typography variant="body2" color="text.secondary">
                Schedules only apply when Record Trigger is Auto (now: {recordTrigger}).
              </Typography>
            )}
          </Grid>

          {deviceConfig.record_schedule_enable && (
            <Grid item xs={12} sx={{ opacity: scheduleActive ? 1 : 0.5 }}>
              {(!deviceConfig.record_schedules || deviceConfig.record_schedules.length === 0) ? (
                <Typography variant="body2" color="text.secondary" sx={{ my: 1, fontStyle: 'italic' }}>
                  No schedule rules added yet. Click "+ Add Schedule Rule" to create one.
                </Typography>
              ) : (
                deviceConfig.record_schedules.map((schedule, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: (schedule.enabled ?? SCHEDULE_DEFAULTS.enabled) ? '#f8fafc' : '#f1f5f9',
                      borderLeft: '4px solid',
                      borderLeftColor: (schedule.enabled ?? SCHEDULE_DEFAULTS.enabled) ? '#2563eb' : '#94a3b8',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={`Rule #${idx + 1}`} size="small" color={(schedule.enabled ?? SCHEDULE_DEFAULTS.enabled) ? "primary" : "default"} />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={schedule.enabled ?? SCHEDULE_DEFAULTS.enabled}
                              onChange={(e) => handleScheduleItemChange(idx, 'enabled', e.target.checked)}
                            />
                          }
                          label={(schedule.enabled ?? SCHEDULE_DEFAULTS.enabled) ? "Enabled" : "Disabled"}
                        />
                      </Box>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveScheduleItem(idx)}
                        title="Remove schedule rule"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Rule Name"
                          value={schedule.name ?? SCHEDULE_DEFAULTS.name}
                          onChange={(e) => handleScheduleItemChange(idx, 'name', e.target.value)}
                          error={scheduleNameCounts[schedule.name ?? SCHEDULE_DEFAULTS.name] > 1}
                          helperText={scheduleNameCounts[schedule.name ?? SCHEDULE_DEFAULTS.name] > 1 ? 'Name must be unique' : ''}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                        />
                      </Grid>

                      <Grid item xs={6} sm={3} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start Time"
                          type="time"
                          value={schedule.start_time || SCHEDULE_DEFAULTS.start_time}
                          onChange={(e) => handleScheduleItemChange(idx, 'start_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                        />
                      </Grid>

                      <Grid item xs={6} sm={3} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End Time"
                          type="time"
                          value={schedule.end_time || SCHEDULE_DEFAULTS.end_time}
                          onChange={(e) => handleScheduleItemChange(idx, 'end_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                        />
                      </Grid>

                      <Grid item xs={6} sm={4} md={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Mode"
                          value={normalizeScheduleMode(schedule.mode ?? SCHEDULE_DEFAULTS.mode)}
                          onChange={(e) => handleScheduleItemChange(idx, 'mode', Number(e.target.value))}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                        >
                          {SCHEDULE_MODE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={4} sm={4} md={1.75}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Threshold (dBA)"
                          type="number"
                          inputProps={{ step: '0.1' }}
                          value={schedule.threshold ?? ''}
                          // Empty means the firmware falls back to spl_upload_threshold
                          placeholder={`default ${deviceConfig.spl_upload_threshold ?? 0}`}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => handleScheduleItemChange(
                            idx,
                            'threshold',
                            e.target.value === '' ? undefined : Number(e.target.value)
                          )}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                        />
                      </Grid>

                      <Grid item xs={4} sm={4} md={1.75}>
                        <TextField
                          fullWidth
                          size="small"
                          label={normalizeScheduleMode(schedule.mode ?? SCHEDULE_DEFAULTS.mode) === 1 ? "Cooldown (sec)" : "Interval (sec)"}
                          type="number"
                          value={schedule.interval ?? SCHEDULE_DEFAULTS.interval}
                          onChange={(e) => handleScheduleItemChange(idx, 'interval', Number(e.target.value))}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        {(() => {
                          const mode = normalizeScheduleMode(schedule.mode ?? SCHEDULE_DEFAULTS.mode);
                          const option = SCHEDULE_MODE_OPTIONS.find((opt) => opt.value === mode);
                          return (
                            <Typography variant="body2" color="text.secondary">
                              {option ? option.help : `Unknown mode ${mode}; the device treats it as 0.`}
                              {(mode === 1 || mode === 2) && (
                                <> Set the threshold above normal background noise (e.g. a train car in motion can exceed 70 dBA){mode === 2 ? ', or this mode will record continuously.' : '.'}</>
                              )}
                            </Typography>
                          );
                        })()}
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Repeat Days (Weekdays bitmask: {schedule.weekdays ?? SCHEDULE_DEFAULTS.weekdays}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {WEEKDAY_OPTIONS.map((day) => {
                            const isSelected = ((schedule.weekdays ?? SCHEDULE_DEFAULTS.weekdays) & day.bit) !== 0;
                            return (
                              <Button
                                key={day.bit}
                                size="small"
                                variant={isSelected ? "contained" : "outlined"}
                                color={isSelected ? "primary" : "inherit"}
                                onClick={() => handleScheduleWeekdayToggle(idx, day.bit)}
                                sx={{
                                  minWidth: 44,
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                }}
                              >
                                {day.label}
                              </Button>
                            );
                          })}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddScheduleItem}
                disabled={(deviceConfig.record_schedules || []).length >= MAX_SCHEDULE_RULES}
                size="small"
                sx={{ mt: 1 }}
              >
                Add Schedule Rule
              </Button>
              {(deviceConfig.record_schedules || []).length >= MAX_SCHEDULE_RULES && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  The device supports at most {MAX_SCHEDULE_RULES} rules; extra rules are ignored.
                </Typography>
              )}
            </Grid>
          )}
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>Sound Settings</Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Tooltip title="Interval in seconds to report SPL data" placement="top-start">
              <TextField
                fullWidth
                label="SPL Report Interval (seconds)"
                name="spl_report_interval"
                type="number"
                value={deviceConfig.spl_report_interval ?? deviceConfig.spl_update_interval ?? 60}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Tooltip>
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ my: 2 }}>Wind Direction Settings</Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={deviceConfig.wind_direction_enable || false}
                  onChange={handleChange}
                  name="wind_direction_enable"
                  color="primary"
                />
              }
              label="Enable Wind Direction Sensor"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Wind Speed ID (wind_speed_id)"
              name="wind_speed_id"
              type="number"
              value={deviceConfig.wind_speed_id ?? 1}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#e6ffed',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Wind Direction ID (wind_direction_id)"
              name="wind_direction_id"
              type="number"
              value={deviceConfig.wind_direction_id ?? 2}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#e6ffed',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Timeout (wind_direction_timeout)"
              name="wind_direction_timeout"
              type="number"
              value={deviceConfig.wind_direction_timeout ?? 10}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#e6ffed',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Baud Rate (wind_baud_rate)"
              name="wind_baud_rate"
              type="number"
              value={deviceConfig.wind_baud_rate ?? 9600}
              onChange={handleChange}
              variant="outlined"
              size="small"
              margin="dense"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#e6ffed',
                },
              }}
            />
          </Grid>
        </Grid>

        {user?.role === "admin" && (
        <>
        <Divider textAlign="left" sx={{ my: 2 }}>MIC Settings</Divider>
        {deviceConfig.mic_config ? (
          // New firmware: nested mic_config; realtek.type is set by firmware and read-only here
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Realtek Mic Type"
                value={deviceConfig.mic_config.realtek?.type ?? 'unknown'}
                helperText="Reported by firmware"
                variant="outlined"
                size="small"
                margin="dense"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} />

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TI Gain"
                type="number"
                value={deviceConfig.mic_config.ti?.gain ?? ''}
                onChange={handleMicChange('ti', 'gain')}
                variant="outlined"
                size="small"
                margin="dense"
                sx={MIC_FIELD_SX}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TI Volume"
                type="number"
                value={deviceConfig.mic_config.ti?.volume ?? ''}
                onChange={handleMicChange('ti', 'volume')}
                variant="outlined"
                size="small"
                margin="dense"
                sx={MIC_FIELD_SX}
              />
            </Grid>

            {deviceConfig.mic_config.realtek?.type === 'dmic_v2s200d' ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="DMIC Gain"
                    value={deviceConfig.mic_config.realtek?.dmic_gain ?? ''}
                    onChange={handleMicChange('realtek', 'dmic_gain')}
                    helperText="Saved here: after reboot. Live gain on the SPL page: immediately"
                    variant="outlined"
                    size="small"
                    margin="dense"
                    sx={MIC_FIELD_SX}
                  >
                    {DMIC_GAIN_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ADC Volume"
                    type="number"
                    value={deviceConfig.mic_config.realtek?.adc_vol ?? ''}
                    onChange={handleMicChange('realtek', 'adc_vol')}
                    helperText={`0–127, 47 = 0 dB (now ${formatDb(adcVolDb(deviceConfig.mic_config.realtek?.adc_vol ?? 47))})`}
                    variant="outlined"
                    size="small"
                    margin="dense"
                    sx={MIC_FIELD_SX}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="High-pass Filter"
                    value={deviceConfig.mic_config.realtek?.hpf_idx ?? ''}
                    onChange={handleMicChange('realtek', 'hpf_idx')}
                    helperText="-2 = auto (~1.25 Hz), -1 = off, 0–7"
                    variant="outlined"
                    size="small"
                    margin="dense"
                    sx={MIC_FIELD_SX}
                  >
                    {HPF_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  {(() => {
                    const totalGain = dmicTotalGainDb(
                      deviceConfig.mic_config.realtek?.dmic_gain,
                      deviceConfig.mic_config.realtek?.adc_vol
                    );
                    return (
                      <Typography variant="body2" color={totalGain >= 24 ? 'error' : 'text.secondary'}>
                        Total gain {formatDb(totalGain)}, clips at ≈ {formatG(clipPeakG(totalGain))} peak
                      </Typography>
                    );
                  })()}
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Realtek Gain"
                    type="number"
                    value={deviceConfig.mic_config.realtek?.gain ?? ''}
                    onChange={handleMicChange('realtek', 'gain')}
                    variant="outlined"
                    size="small"
                    margin="dense"
                    sx={MIC_FIELD_SX}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Realtek Volume"
                    type="number"
                    value={deviceConfig.mic_config.realtek?.volume ?? ''}
                    onChange={handleMicChange('realtek', 'volume')}
                    helperText="0–127"
                    variant="outlined"
                    size="small"
                    margin="dense"
                    sx={MIC_FIELD_SX}
                  />
                </Grid>
              </>
            )}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Realtek Gain"
                name="realtek_gain"
                type="number"
                value={deviceConfig.realtek_gain || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Realtek Volume"
                name="realtek_volume"
                type="number"
                value={deviceConfig.realtek_volume || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TI Gain"
                name="ti_gain"
                type="number"
                value={deviceConfig.ti_gain || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TI Volume"
                name="ti_volume"
                type="number"
                value={deviceConfig.ti_volume || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>
          </Grid>
        )}
        </>
        )}

        <Divider textAlign="left" sx={{ my: 2 }}>WiFi Settings</Divider>
        <FormControlLabel
          control={
            <Switch
              checked={deviceConfig.wifi_enable || false}
              onChange={handleChange}
              name="wifi_enable"
            />
          }
          label="Enable WiFi"
        />

        {deviceConfig.wifi_enable && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="WiFi SSID"
                name="wifi_ssid"
                value={deviceConfig.wifi_ssid || ''}
                onChange={handleChange}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="WiFi Password"
                name="wifi_password"
                value={deviceConfig.wifi_password || ''}
                onChange={handleChange}
                type="password"
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        <Divider textAlign="left" sx={{ my: 2 }}>Ethernet Settings</Divider>
        <FormControlLabel
          control={
            <Switch
              checked={deviceConfig.ethernet_enable || false}
              onChange={handleChange}
              name="ethernet_enable"
              color="primary"
            />
          }
          label="Enable Ethernet"
        />

        {deviceConfig.ethernet_enable && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deviceConfig.ethernet_dhcp ?? true}
                    onChange={handleChange}
                    name="ethernet_dhcp"
                    color="primary"
                  />
                }
                label="Enable DHCP"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ethernet IP"
                name="ethernet_ip"
                value={deviceConfig.ethernet_ip || ''}
                onChange={handleChange}
                disabled={deviceConfig.ethernet_dhcp}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Subnet Mask"
                name="ethernet_mask"
                value={deviceConfig.ethernet_mask || ''}
                onChange={handleChange}
                disabled={deviceConfig.ethernet_dhcp}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gateway"
                name="ethernet_gateway"
                value={deviceConfig.ethernet_gateway || ''}
                onChange={handleChange}
                disabled={deviceConfig.ethernet_dhcp}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="DNS Server"
                name="ethernet_dns"
                value={deviceConfig.ethernet_dns || ''}
                onChange={handleChange}
                disabled={deviceConfig.ethernet_dhcp}
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e6ffed',
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        {!onSaveSettings && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
});

const DeviceSettingsDialog = ({ open, onClose, device, onSave }) => {
  const deviceSettingSubmitRef = React.useRef();

  const handleSave = async () => {
    if (deviceSettingSubmitRef.current) {
      const success = await deviceSettingSubmitRef.current.submit();
      if (success) {
        onSave();
        onClose();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Device Settings: {device?.client_name || device?.clientId}</DialogTitle>
      <DialogContent dividers>
        <DeviceSetting
          ref={deviceSettingSubmitRef}
          device={device}
          onSaveSettings={(config) => {
            console.log("onSaveSettings called with:", config);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSave}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceSetting;
export { DeviceSettingsDialog };